/**
 * The stepped character builder (creation and the free rebuild before the first delve):
 * Ancestry → Background → Class → Attributes → Skills → Feats → Spells → Review.
 * Every choice is the player's; the server checks them (`previewBuild`) and the live sheet shows what
 * they make. A choice an earlier change made invalid stays selected and is marked, never cleared.
 */
import type { BuildChoices, BuildPreview, ClientMessage, Command, ServerMessage } from '../shared/protocol.ts';
import type { Codex } from '../shared/view.ts';
import { ATTRIBUTES, ATTRIBUTE_NAMES, type Attribute } from '../engine/rules/attributes.ts';
import { SKILLS, SKILL_IDS, type SkillId } from '../engine/rules/proficiency.ts';
import { applyControl, controlsHtml, listHtml, newListState, type ListCfg, type ListState, type PickOpt } from './picklist.ts';
import { blockHtml } from './rules.ts';

type Welcome = Extract<ServerMessage, { t: 'welcome' }>;
type Step = 'ancestry' | 'background' | 'class' | 'attributes' | 'skills' | 'feats' | 'spells' | 'review';

const STEPS: { id: Step; label: string }[] = [
  { id: 'ancestry', label: 'Ancestry' }, { id: 'background', label: 'Background' }, { id: 'class', label: 'Class' }, { id: 'attributes', label: 'Attributes' },
  { id: 'skills', label: 'Skills' }, { id: 'feats', label: 'Feats' }, { id: 'spells', label: 'Spells' }, { id: 'review', label: 'Review' },
];

export interface BuilderDeps {
  root: HTMLElement;
  welcome: () => Welcome | null;
  send: (msg: ClientMessage) => void;
  cmd: (cmd: Command) => void;
  toast: (message: string) => void;
}

interface State {
  mode: 'create' | 'rebuild';
  step: Step;
  choices: BuildChoices;
  preview: BuildPreview | null;
  /** A preview is on its way for the latest choices. */
  waiting: boolean;
  visited: Set<Step>;
  lists: Record<string, ListState>;
  awaitingSuggest: boolean;
  awaitingRebuild: boolean;
  summaryOpen: boolean;
}

const blank = (): BuildChoices => ({
  name: '', ancestryId: '', ancestryBoostMode: 'standard', ancestryBoosts: [], backgroundId: '', backgroundBoosts: [], classId: '',
  freeBoosts: [], skills: [], ancestryFeat: '', featurePicks: {}, spells: [],
});

let deps: BuilderDeps;
let st: State | null = null;
let timer = 0;
/** The lists on screen, by key, so a filter change redraws only its own list. */
const sources = new Map<string, () => { opts: PickOpt[]; cfg: ListCfg }>();

const esc = (s: unknown): string => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
const $ = <T extends HTMLElement = HTMLElement>(id: string) => deps.root.querySelector<T>(`#${id}`)!;
const signed = (n: number) => (n >= 0 ? `+${n}` : `${n}`);
const attrName = (a: string) => ATTRIBUTE_NAMES[a as Attribute] ?? a;
const skillName = (s: string) => SKILLS[s as SkillId]?.name ?? s;
const codex = (): Codex | null => deps.welcome()?.codex ?? null;
const featName = (id: string) => codex()?.[`feat:${id}`]?.name ?? id;

export function installBuilder(d: BuilderDeps): void {
  deps = d;
  d.root.addEventListener('click', onClick);
  d.root.addEventListener('input', onInput);
  d.root.addEventListener('change', onInput);
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && st && !(e.target instanceof HTMLInputElement)) closeBuilder();
  });
}

export const builderOpen = (): boolean => st !== null;

/** Opens the builder blank (a new character), or with the choices of a character about to be rebuilt. */
export function openBuilder(mode: 'create' | 'rebuild', from?: BuildChoices): void {
  st = {
    mode, step: 'ancestry', choices: from ? structuredClone(from) : blank(), preview: null, waiting: true,
    visited: new Set(mode === 'rebuild' ? STEPS.map((s) => s.id) : ['ancestry']), lists: {}, awaitingSuggest: false, awaitingRebuild: false, summaryOpen: false,
  };
  deps.root.classList.remove('hidden');
  document.body.classList.add('builder-open');
  renderShell();
  requestPreview();
}

export function closeBuilder(): void {
  st = null;
  window.clearTimeout(timer);
  deps.root.classList.add('hidden');
  deps.root.innerHTML = '';
  document.body.classList.remove('builder-open');
}

/** The server's answer to the last previewBuild. */
export function onBuildPreview(p: BuildPreview): void {
  if (!st) return;
  st.preview = p;
  st.waiting = false;
  if (st.awaitingSuggest) {
    st.awaitingSuggest = false;
    if (p.suggested) {
      st.choices = { ...p.suggested, name: st.choices.name };
      for (const s of STEPS) st.visited.add(s.id);
      st.waiting = true;
      requestPreview();
    } else {
      deps.toast(p.errors[0]?.message ?? 'Choose an ancestry, a background and a class first');
    }
  }
  renderAll();
}

/** A refused create or rebuild leaves the builder open. */
export function onBuilderError(): void {
  if (st) st.awaitingRebuild = false;
}

/** A new view arrived: a rebuild we sent has gone through. */
export function onBuilderState(): void {
  if (st?.awaitingRebuild) closeBuilder();
}

// ------------------------------------------------------------ choices

function requestPreview(): void {
  if (!st) return;
  st.waiting = true;
  window.clearTimeout(timer);
  timer = window.setTimeout(() => {
    if (st) deps.send({ t: 'previewBuild', choices: st.choices });
  }, 120);
}

/** Changes the choices, keeps derived fields consistent and asks for a new preview. */
function change(fn: (c: BuildChoices) => void): void {
  if (!st) return;
  fn(st.choices);
  if (st.choices.backgroundSkillSwap !== undefined && !swapNeeded()) delete st.choices.backgroundSkillSwap;
  requestPreview();
  renderAll();
}

const cls = () => deps.welcome()?.classes.find((c) => c.id === st?.choices.classId);
const ancestry = () => deps.welcome()?.ancestries.find((a) => a.id === st?.choices.ancestryId);
const background = () => deps.welcome()?.backgrounds.find((b) => b.id === st?.choices.backgroundId);

/** The skills the class trains: its fixed ones, and the "one of" choices made so far. */
function classTrained(): Set<string> {
  const out = new Set<string>();
  const c = cls();
  for (const s of c?.build?.fixedSkills ?? []) if (typeof s === 'string') out.add(s);
  for (const s of st?.choices.classSkills ?? []) if (s) out.add(s);
  return out;
}

/** The background's skill is one the class trains: the player picks another skill instead. */
function swapNeeded(): boolean {
  const b = background();
  return !!b && classTrained().has(b.skill);
}

function trainedSkills(): Set<string> {
  const out = classTrained();
  const b = background();
  if (b) {
    if (swapNeeded()) {
      if (st?.choices.backgroundSkillSwap) out.add(st.choices.backgroundSkillSwap);
    } else out.add(b.skill);
  }
  return out;
}

function toggleIn(list: string[], v: string, count: number): string[] {
  if (list.includes(v)) return list.filter((x) => x !== v);
  const next = [...list, v];
  while (next.length > count) next.shift();
  return next;
}

function setPick(key: string, id: string): void {
  change((c) => {
    if (key === 'ancestry-feat') c.ancestryFeat = id;
    else if (key === 'class-feat') c.classFeat = id;
    else if (key === 'skill-feat') c.skillFeat = id;
    else c.featurePicks = { ...c.featurePicks, [key]: id };
  });
}

function answerOf(key: string): string | undefined {
  const c = st!.choices;
  return key === 'ancestry-feat' ? c.ancestryFeat || undefined : key === 'class-feat' ? c.classFeat : key === 'skill-feat' ? c.skillFeat : c.featurePicks[key];
}

// ------------------------------------------------------------ validation flags

const errorsOf = (step: Step) => (st?.preview?.errors ?? []).filter((e) => e.step === step);

/** 'bad': visited and invalid or incomplete; 'ok': visited and fine; '': not visited yet. */
function stepFlag(step: Step): 'bad' | 'ok' | '' {
  if (!st?.visited.has(step) || (!st.preview && step !== 'ancestry')) return '';
  if (!st.preview) return '';
  return (step === 'review' ? st.preview.errors : errorsOf(step)).length ? 'bad' : 'ok';
}

// ------------------------------------------------------------ rendering

function renderShell(): void {
  if (!st) return;
  deps.root.innerHTML = `
    <div class="b-box">
      <div class="b-head">
        <h1>${st.mode === 'rebuild' ? 'Rebuild Your Character' : 'New Character'}</h1>
        <label class="b-name">Name <input id="b-name" maxlength="24" value="${esc(st.choices.name)}" autocomplete="off"></label>
        <button type="button" class="small" data-b-suggest title="Fill every step with a sensible build for the ancestry, background and class you've chosen">Suggest a Build</button>
        <span class="spacer"></span>
        <button type="button" class="ghost" data-b-close>Cancel</button>
      </div>
      <nav id="b-rail" class="b-rail" aria-label="Builder steps"></nav>
      <div id="b-bar" class="b-bar"></div>
      <div class="b-cols">
        <div id="b-body" class="b-body"></div>
        <aside id="b-aside" class="b-aside"></aside>
      </div>
      <div id="b-foot" class="b-foot"></div>
    </div>`;
  renderAll();
}

function renderAll(): void {
  if (!st) return;
  renderRail();
  renderMain();
  renderAside();
  renderFoot();
}

function renderRail(): void {
  if (!st) return;
  $('b-rail').innerHTML = STEPS.map((s, i) => {
    const flag = stepFlag(s.id);
    const n = (s.id === "review" ? st!.preview?.errors ?? [] : errorsOf(s.id)).length;
    return `<button type="button" class="b-step ${st!.step === s.id ? 'current' : ''} ${flag}" data-b-step="${s.id}" ${st!.step === s.id ? 'aria-current="step"' : ''}>
      <span class="b-num">${flag === 'ok' ? '✓' : flag === 'bad' ? '!' : i + 1}</span><span class="b-label">${s.label}</span>${flag === 'bad' ? `<span class="b-count" title="${n} to fix">${n}</span>` : ''}</button>`;
  }).join('');
  $('b-rail').querySelector('.current')?.scrollIntoView({ block: 'nearest', inline: 'center' });
}

function renderFoot(): void {
  if (!st) return;
  const i = STEPS.findIndex((s) => s.id === st!.step);
  const errors = st.preview?.errors ?? [];
  const ready = !!st.preview && !st.waiting && !errors.length;
  const last = i === STEPS.length - 1;
  $('b-foot').innerHTML = `
    <button type="button" ${i === 0 ? 'disabled' : ''} data-b-back>Back</button>
    <span class="grow muted">${st.waiting ? 'Checking…' : errors.length ? `${errors.length} thing${errors.length === 1 ? '' : 's'} left to choose or fix` : 'Everything is chosen'}</span>
    ${last ? '' : '<button type="button" data-b-next>Next</button>'}
    <button type="button" class="primary" data-b-create ${ready ? '' : 'disabled'} title="${ready ? '' : 'Every step must be complete and valid'}">${st.mode === 'rebuild' ? 'Rebuild Character' : 'Create Character'}</button>`;
}

function sheetHtml(): string {
  if (!st) return '';
  const p = st.preview;
  const s = p?.sheet;
  if (!s) return '<p class="muted">Choose an ancestry, a background and a class to see your character take shape.</p>';
  const c = st.choices;
  const trained = s.skills.filter((k) => k.rank > 0);
  return `<div class="b-sheet ${st.waiting ? 'waiting' : ''}">
    <div class="b-sheet-title"><b>${esc(c.name || 'Unnamed')}</b><br><span class="muted">${esc(ancestry()?.name ?? '')} ${esc(cls()?.name ?? '')} 1</span></div>
    <div class="stats">
      <div class="stat"><b>${s.hp}</b><span>HP</span></div><div class="stat"><b>${s.ac}</b><span>AC</span></div>
      <div class="stat"><b>${signed(s.fort)}</b><span>Fort</span></div><div class="stat"><b>${signed(s.ref)}</b><span>Ref</span></div>
      <div class="stat"><b>${signed(s.will)}</b><span>Will</span></div><div class="stat"><b>${signed(s.perception)}</b><span>Perc</span></div>
    </div>
    <div class="attrs">${ATTRIBUTES.map((a) => `<div class="attr"><span class="muted">${a.toUpperCase()}</span><br><b>${signed(s.attributes[a])}</b></div>`).join('')}</div>
    <h3>Skills</h3><div class="muted">${trained.length ? trained.map((k) => `${esc(k.name)} ${signed(k.total)}`).join(' · ') : 'None yet'}</div>
    <h3>Feats</h3><div class="muted">${s.feats.length ? s.feats.map((f) => esc(featName(f))).join(' · ') : 'None yet'}</div>
    ${s.spells.length ? `<h3>Spells</h3><div class="muted">${s.spells.map((f) => esc(codex()?.[`spell:${f}`]?.name ?? f)).join(' · ')}</div>` : ''}
  </div>`;
}

function renderAside(): void {
  if (!st) return;
  const s = st.preview?.sheet;
  const html = sheetHtml();
  $('b-aside').innerHTML = `<h2>Your Character</h2>${html}`;
  $('b-bar').innerHTML = `<button type="button" class="b-bar-toggle" data-b-summary aria-expanded="${st.summaryOpen}">
      <span>${s ? `HP ${s.hp} · AC ${s.ac} · ${ATTRIBUTES.map((a) => `${a.toUpperCase()} ${signed(s.attributes[a])}`).join(' ')}` : 'Your character'}</span><span>${st.summaryOpen ? '▴' : '▾'}</span></button>
    ${st.summaryOpen ? `<div class="b-bar-open">${html}</div>` : ''}`;
}

/** An answer the build no longer asks for (an earlier change made it stale): it stays, flagged, until the player removes it. */
const STRAY = /Not a choice this character has|has no skill to choose|has no spells to choose|No skill to replace/;
const FIELD_LABEL: Record<string, string> = { ancestryFeat: 'Ancestry feat', classFeat: 'Class feat', skillFeat: 'Skill feat', classSkills: 'Class skill', spells: 'Spells', backgroundSkillSwap: 'Background skill' };

function errorRow(x: { field: string; message: string }): string {
  if (!STRAY.test(x.message)) return `<li>${esc(x.message)}</li>`;
  const label = FIELD_LABEL[x.field] ?? featureTitle(x.field);
  return `<li class="stale">${esc(label)}: ${esc(x.message === 'Not a choice this character has' ? 'no longer part of this build' : x.message)} <button type="button" class="small ghost" data-b-drop="${esc(x.field)}">Remove it</button></li>`;
}

function stepErrors(step: Step): string {
  if (!st?.visited.has(step)) return '';
  const e = errorsOf(step);
  return e.length ? `<ul class="b-errors">${e.map(errorRow).join('')}</ul>` : '';
}

function fieldBad(step: Step, field: string): boolean {
  return errorsOf(step).some((e) => e.field === field);
}

function renderMain(): void {
  if (!st || !deps.welcome()) return;
  st.visited.add(st.step);
  sources.clear();
  const body = $('b-body');
  const top = body.scrollTop;
  const html = ({ ancestry: ancestryStep, background: backgroundStep, class: classStep, attributes: attributesStep, skills: skillsStep, feats: featsStep, spells: spellsStep, review: reviewStep } satisfies Record<Step, () => string>)[st.step]();
  body.innerHTML = html;
  body.scrollTop = top;
}

/** A row of six attribute buttons: pick `count` (different ones); `blocked` ones can't be taken. */
function attrPicker(field: string, selected: readonly string[], count: number, blocked: readonly string[] = [], only?: readonly string[], bad = false): string {
  return `<div class="b-attrs ${bad ? 'bad' : ''}" role="group">${ATTRIBUTES.map((a) => {
    const on = selected.includes(a);
    const off = blocked.includes(a) || (only !== undefined && !only.includes(a));
    return `<button type="button" class="choice b-attr ${on ? 'selected' : ''}" ${off && !on ? 'disabled' : ''} data-b-attr="${field}:${a}"><b>${a.toUpperCase()}</b><span>${attrName(a)}</span></button>`;
  }).join('')}</div><div class="muted b-hint">${selected.length}/${count} chosen</div>`;
}

function chips(items: string[]): string {
  return `<span class="b-chips">${items.map((i) => `<span class="trait">${esc(i)}</span>`).join('')}</span>`;
}

function ancestryStep(): string {
  const w = deps.welcome()!;
  const c = st!.choices;
  const sel = ancestry();
  const list = w.ancestries.map((a) => {
    const info = a.ancestry;
    const extra = info ? chips([...info.boosts.map((b) => (b === 'free' ? 'Free boost' : `+${attrName(b)}`)), ...info.flaws.map((f) => `−${attrName(f)}`), `${info.hp} HP`]) : '';
    return `<button type="button" class="choice ${a.id === c.ancestryId ? 'selected' : ''}" data-b-ancestry="${esc(a.id)}"><b>${esc(a.name)}</b><span>${esc(a.description)}</span>${extra}</button>`;
  }).join('');
  let boosts = '';
  if (sel?.ancestry) {
    const fixed = sel.ancestry.boosts.filter((b) => b !== 'free');
    const free = sel.ancestry.boosts.length - fixed.length;
    const standard = c.ancestryBoostMode === 'standard';
    const need = standard ? free : 2;
    boosts = `<h3>Attribute boosts</h3>
      <div class="b-modes">
        <button type="button" class="choice ${standard ? 'selected' : ''}" data-b-mode="standard"><b>Standard</b><span>${[...fixed.map((f) => `+${attrName(f)}`), ...(free ? [`${free} free boost${free === 1 ? '' : 's'}`] : []), ...sel.ancestry.flaws.map((f) => `−${attrName(f)} (flaw)`)].join(', ')}</span></button>
        <button type="button" class="choice ${standard ? '' : 'selected'}" data-b-mode="free"><b>Two free boosts</b><span>No flaw, no fixed boosts: two boosts to different attributes of your choice.</span></button>
      </div>
      <p class="muted">Choose ${need} ${standard ? 'free ' : ''}boost${need === 1 ? '' : 's'}${standard && fixed.length ? ` (not ${fixed.map(attrName).join(' or ')}, which the ancestry already boosts)` : ''}.</p>
      ${attrPicker('ancestry', c.ancestryBoosts, need, standard ? fixed : [], undefined, fieldBad('ancestry', 'ancestryBoosts'))}`;
  }
  const block = sel && w.codex[`ancestry:${sel.id}`] ? `<div class="b-block">${blockHtml(w.codex[`ancestry:${sel.id}`], { compact: true })}</div>` : '';
  return `<h2>Ancestry</h2>${stepErrors('ancestry')}<div class="choices b-grid">${list}</div>${boosts}${block}`;
}

function backgroundStep(): string {
  const w = deps.welcome()!;
  const c = st!.choices;
  const sel = background();
  const list = w.backgrounds.map((b) => `<button type="button" class="choice ${b.id === c.backgroundId ? 'selected' : ''}" data-b-bg="${esc(b.id)}"><b>${esc(b.name)}</b><span>${esc(b.description)}</span>${chips([...b.boosts.map((a) => `+${attrName(a)}`), `${skillName(b.skill)}`, featName(b.skillFeat)])}</button>`).join('');
  let boosts = '';
  if (sel) {
    const [a, b] = c.backgroundBoosts;
    boosts = `<h3>Attribute boosts</h3>
      <p class="muted">One boost to ${sel.boosts.map(attrName).join(' or ')}, and one free boost to a different attribute.</p>
      ${attrPicker('bg0', a ? [a] : [], 1, [], sel.boosts, fieldBad('background', 'backgroundBoosts') && !a)}
      <p class="muted">Free boost</p>
      ${attrPicker('bg1', b ? [b] : [], 1, a ? [a] : ATTRIBUTES, undefined, fieldBad('background', 'backgroundBoosts') && !b)}
      <p class="muted">Trained in ${esc(skillName(sel.skill))}, with the skill feat ${esc(featName(sel.skillFeat))}.</p>`;
  }
  return `<h2>Background</h2>${stepErrors('background')}<div class="choices b-grid">${list}</div>${boosts}`;
}

function classStep(): string {
  const w = deps.welcome()!;
  const c = st!.choices;
  const sel = cls();
  const list = w.classes.map((k) => `<button type="button" class="choice ${k.id === c.classId ? 'selected' : ''}" data-b-class="${esc(k.id)}"><b>${esc(k.name)}</b><span>${esc(k.description)}</span></button>`).join('');
  let more = '';
  if (sel) {
    const keys = sel.keyOptions ?? [];
    if (keys.length) {
      more += `<h3>Key attribute</h3><div class="key-choices">${keys.map((k) => `<button type="button" class="choice ${k.attribute === c.keyAttribute ? 'selected' : ''}" data-b-key="${esc(k.attribute)}"><b>${esc(k.name)}</b><span>${k.skill ? `Trained in ${esc(k.skill)}` : k.note ? esc(k.note) : ''}</span></button>`).join('')}</div>`;
    }
    const b = sel.build;
    if (b) {
      const fixed = b.fixedSkills.map((s) => (Array.isArray(s) ? s.map(skillName).join(' or ') : skillName(s)));
      more += `<p class="muted">${b.hp} Hit Points per level · key attribute ${attrName(c.keyAttribute ?? b.keyAttribute)} · trained in ${fixed.length ? fixed.join(', ') : 'no fixed skills'} and ${b.freeSkills} more skill${b.freeSkills === 1 ? '' : 's'} (plus your Intelligence modifier).</p>`;
    }
    for (const p of (st!.preview?.picks ?? []).filter((x) => x.step === 'class')) more += pickSection(p.key, p.title ?? featureTitle(p.key), p.options, p.chosen, false);
  }
  const block = sel && w.codex[`class:${sel.id}`] ? `<div class="b-block">${blockHtml(w.codex[`class:${sel.id}`], { compact: true })}</div>` : '';
  return `<h2>Class</h2>${stepErrors('class')}<div class="choices b-grid">${list}</div>${more}${block}`;
}

function featureTitle(key: string): string {
  return codex()?.[`feat:${key}`]?.name ?? codex()?.[`classfeature:${st?.choices.classId}:${key}`]?.name ?? key.replace(/#\d+$/, '').replace(/-/g, ' ').replace(/^./, (x) => x.toUpperCase());
}

/** One searchable pick: its heading, filters and list. */
function pickSection(key: string, title: string, options: PickOpt[], chosen: string | undefined, spell: boolean): string {
  const state = (st!.lists[key] ??= newListState());
  const stale = chosen && options.find((o) => o.id === chosen && !o.eligible) ? [chosen] : [];
  const cfg = (): ListCfg => ({ spell, codex: codex(), mode: 'choose', attr: 'b-pick', value: (id) => `${key}|${id}`, chosen: chosen ? [chosen] : [], stale });
  sources.set(key, () => ({ opts: options, cfg: cfg() }));
  const bad = errorsOf('feats').concat(errorsOf('class')).some((e) => e.field === key);
  return `<section class="b-pick ${bad ? 'bad' : ''}"><h3>${esc(title)} ${chosen ? '' : '<span class="b-need">choose one</span>'}</h3>
    <div class="pl" data-pl-box="${esc(key)}">${controlsHtml(options, state, cfg(), key)}<div class="pl-list">${listHtml(options, state, cfg())}</div></div></section>`;
}

function attributesStep(): string {
  const c = st!.choices;
  const s = st!.preview?.sheet;
  const from = new Map<string, string[]>();
  const add = (a: string, tag: string) => from.set(a, [...(from.get(a) ?? []), tag]);
  const anc = ancestry()?.ancestry;
  if (anc) {
    if (c.ancestryBoostMode === 'standard') {
      for (const b of anc.boosts) if (b !== 'free') add(b, 'Ancestry');
      for (const f of anc.flaws) add(f, 'Flaw');
    }
    for (const b of c.ancestryBoosts) add(b, 'Ancestry');
  }
  for (const b of c.backgroundBoosts) add(b, 'Background');
  const k = c.keyAttribute ?? cls()?.build?.keyAttribute;
  if (k && c.classId) add(k, 'Class');
  for (const b of c.freeBoosts) add(b, 'Free');
  const rows = ATTRIBUTES.map((a) => `<div class="b-attr-row"><b>${a.toUpperCase()}</b><span class="grow muted">${esc(attrName(a))} · ${(from.get(a) ?? []).map(esc).join(', ') || 'no boosts'}</span><b>${s ? signed(s.attributes[a]) : ''}</b></div>`).join('');
  return `<h2>Attributes</h2>${stepErrors('attributes')}
    <p class="muted">Your ancestry, background and class have already boosted some attributes. Choose four free boosts to four different attributes. No attribute can start above +4.</p>
    ${attrPicker('free', c.freeBoosts, 4, [], undefined, fieldBad('attributes', 'freeBoosts'))}
    <h3>Your modifiers</h3><div class="b-attr-table">${rows}</div>`;
}

function skillsStep(): string {
  const p = st!.preview;
  const c = st!.choices;
  if (!p?.skillChoices) return `<h2>Skills</h2>${stepErrors('skills')}<p class="muted">Choose an ancestry, a background and a class first.</p>`;
  const groups = p.skillChoices.classSkills;
  let html = `<h2>Skills</h2>${stepErrors('skills')}`;
  groups.forEach((g, i) => {
    const on = c.classSkills?.[i];
    html += `<h3>Class skill</h3><p class="muted">Your class trains one of these:</p><div class="b-skills">${g.map((s) => `<button type="button" class="choice ${on === s ? 'selected' : ''}" data-b-classskill="${i}:${s}"><b>${esc(skillName(s))}</b></button>`).join('')}</div>`;
  });
  const b = background();
  if (b && swapNeeded()) {
    const t = trainedSkills();
    const swap = c.backgroundSkillSwap;
    html += `<h3>Background skill</h3><p class="muted">Your background trains ${esc(skillName(b.skill))}, which your class already trains. Choose another skill instead.</p>
      <div class="b-skills ${fieldBad('skills', 'backgroundSkillSwap') ? 'bad' : ''}">${SKILL_IDS.map((s) => `<button type="button" class="choice ${swap === s ? 'selected' : ''}" ${t.has(s) && swap !== s ? 'disabled' : ''} data-b-swap="${s}"><b>${esc(skillName(s))}</b></button>`).join('')}</div>`;
  } else if (b) {
    html += `<p class="muted">Your background trains ${esc(skillName(b.skill))}.</p>`;
  }
  const t = trainedSkills();
  const count = p.skillChoices.freeCount;
  const free = c.skills;
  html += `<h3>Free skills</h3><p class="muted">Choose ${count} (your class's free skills plus your Intelligence modifier), all different. Skills you already train can't be chosen again.</p>
    <div class="b-skills ${fieldBad('skills', 'skills') ? 'bad' : ''}">${SKILL_IDS.map((s) => {
      const taken = t.has(s) && !free.includes(s);
      return `<button type="button" class="choice ${free.includes(s) ? 'selected' : ''} ${free.includes(s) && t.has(s) ? 'stale' : ''}" ${taken ? 'disabled' : ''} data-b-skill="${s}"><b>${esc(skillName(s))}</b><span>${SKILLS[s].attribute.toUpperCase()}</span></button>`;
    }).join('')}</div><div class="muted b-hint">${free.length}/${count} chosen</div>`;
  return html;
}

const PICK_TITLE: Record<string, string> = { 'ancestry-feat': 'Ancestry feat (level 1)', 'class-feat': 'Class feat (level 1)', 'skill-feat': 'Skill feat (level 1)' };

function featsStep(): string {
  const p = st!.preview;
  const picks = (p?.picks ?? []).filter((x) => x.step === 'feats');
  const bg = background();
  let html = `<h2>Feats</h2>${stepErrors('feats')}`;
  if (bg) html += `<p class="muted">Your background gives you the skill feat <b class="ref" data-ref="feat:${esc(bg.skillFeat)}">${esc(featName(bg.skillFeat))}</b>.</p>`;
  if (!p?.sheet) return `${html}<p class="muted">Choose an ancestry, a background and a class first.</p>`;
  if (!picks.length) html += '<p class="muted">This character has no feats to choose at 1st level.</p>';
  for (const pk of picks) html += pickSection(pk.key, PICK_TITLE[pk.key] ?? pk.title ?? featureTitle(pk.key), pk.options, answerOf(pk.key), false);
  return html;
}

function spellsStep(): string {
  const p = st!.preview;
  const c = st!.choices;
  let html = `<h2>Spells</h2>${stepErrors('spells')}`;
  // Spells a class feature gives a choice of (a school's curriculum spell) come first.
  const extra = (p?.picks ?? []).filter((x) => x.step === 'spells');
  for (const pk of extra) html += pickSection(pk.key, pk.title ?? featureTitle(pk.key), pk.options, answerOf(pk.key), true);
  const sp = p?.spells;
  if (!sp && c.spells.length) html += `<p class="b-stale">Chosen before you changed your class: ${c.spells.map((x) => esc(codex()?.[`spell:${x}`]?.name ?? x)).join(', ')}.</p>`;
  if (!sp) return `${html}${extra.length ? '' : `<p class="muted">${p?.sheet ? 'This class casts no spells.' : 'Choose an ancestry, a background and a class first.'}</p>`}`;
  const total = sp.cantrips + sp.rank1 + sp.extra;
  const rankOf = (id: string) => /^Cantrip/.test(codex()?.[`spell:${id}`]?.type ?? '') ? 0 : 1;
  const nc = c.spells.filter((s) => rankOf(s) === 0).length;
  html += `<p class="muted">Choose ${total} spells: at least ${sp.cantrips} cantrips and ${sp.rank1} 1st-rank spell${sp.rank1 === 1 ? '' : 's'}${sp.extra ? `, and one more of either` : ''}. Chosen: ${c.spells.length}/${total} (${nc} cantrips).</p>`;
  const state = (st!.lists.spells ??= newListState());
  const chosen = c.spells;
  const options: PickOpt[] = sp.options.map((o) => ({ ...o, level: rankOf(o.id) === 0 ? 0 : undefined }));
  const cfg = (): ListCfg => ({ spell: true, codex: codex(), mode: 'choose', attr: 'b-spell', value: (id) => id, chosen });
  sources.set('spells', () => ({ opts: options, cfg: cfg() }));
  html += `<div class="pl ${fieldBad('spells', 'spells') ? 'bad' : ''}" data-pl-box="spells">${controlsHtml(options, state, cfg(), 'spells')}<div class="pl-list">${listHtml(options, state, cfg())}</div></div>`;
  return html;
}

function reviewStep(): string {
  const p = st!.preview;
  const errors = p?.errors ?? [];
  const c = st!.choices;
  const row = (label: string, value: string) => `<div class="b-review-row"><b>${label}</b><span>${value || '<span class="muted">not chosen</span>'}</span></div>`;
  const s = p?.sheet;
  const picks = (p?.picks ?? []).map((x) => answerOf(x.key)).filter((x): x is string => !!x).map(featName);
  return `<h2>Review</h2>
    ${errors.length ? `<div class="b-errors"><b>To fix before you can create this character:</b><ul>${errors.map((e) => `<li>${STRAY.test(e.message) ? esc(`${FIELD_LABEL[e.field] ?? featureTitle(e.field)}: no longer part of this build`) : esc(e.message)} <button type="button" class="small ghost" data-b-step="${e.step}">Go to ${STEPS.find((x) => x.id === e.step)?.label}</button></li>`).join('')}</ul></div>` : '<p class="good">Everything is chosen. This character is ready.</p>'}
    ${row('Name', esc(c.name))}
    ${row('Ancestry', esc(ancestry()?.name ?? ''))}
    ${row('Background', esc(background()?.name ?? ''))}
    ${row('Class', esc(`${cls()?.name ?? ''}${c.keyAttribute && (cls()?.keyOptions?.length ?? 0) ? ` (${attrName(c.keyAttribute)})` : ''}`))}
    ${row('Attributes', s ? ATTRIBUTES.map((a) => `${a.toUpperCase()} ${signed(s.attributes[a])}`).join(' · ') : '')}
    ${row('Skills', s ? s.skills.filter((k) => k.rank > 0).map((k) => esc(k.name)).join(', ') : '')}
    ${row('Feats', [...(background() ? [featName(background()!.skillFeat)] : []), ...picks].map(esc).join(', '))}
    ${p?.spells ? row('Spells', c.spells.map((x) => esc(codex()?.[`spell:${x}`]?.name ?? x)).join(', ')) : ''}
    ${st!.mode === 'rebuild' ? '<p class="muted">Rebuilding keeps your gear, gold and experience. If your class changes, your old kit is swapped for the new class\'s kit as long as you still own all of it.</p>' : ''}`;
}

// ------------------------------------------------------------ events

function onInput(e: Event): void {
  if (!st) return;
  const t = e.target as HTMLElement;
  if (t.id === 'b-name') {
    st.choices.name = (t as HTMLInputElement).value;
    requestPreview();
    return renderFoot();
  }
  const key = t.dataset.pl;
  if (key === undefined) return;
  const state = (st.lists[key] ??= newListState());
  if (!applyControl(state, t)) return;
  const field = t.dataset.plField;
  const box = deps.root.querySelector<HTMLElement>(`[data-pl-box="${CSS.escape(key)}"]`);
  const src = sources.get(key)?.();
  if (!box || !src) return;
  // Typing redraws only the list, so the search box keeps focus; a filter redraws the controls too.
  if (field === 'search') box.querySelector('.pl-list')!.innerHTML = listHtml(src.opts, state, src.cfg);
  else box.innerHTML = `${controlsHtml(src.opts, state, src.cfg, key)}<div class="pl-list">${listHtml(src.opts, state, src.cfg)}</div>`;
}

function go(step: Step): void {
  if (!st) return;
  st.step = step;
  st.visited.add(step);
  renderAll();
  $('b-body').scrollTop = 0;
}

function onClick(e: MouseEvent): void {
  if (!st) return;
  const el = (e.target as HTMLElement).closest<HTMLElement>('[data-b-close],[data-b-step],[data-b-next],[data-b-back],[data-b-create],[data-b-suggest],[data-b-summary],[data-b-ancestry],[data-b-mode],[data-b-attr],[data-b-bg],[data-b-class],[data-b-key],[data-b-pick],[data-b-classskill],[data-b-swap],[data-b-skill],[data-b-spell],[data-b-drop]');
  if (!el) return;
  const d = el.dataset;
  const c = st.choices;
  if (d.bClose !== undefined) return closeBuilder();
  if (d.bStep) return go(d.bStep as Step);
  if (d.bNext !== undefined) return go(STEPS[Math.min(STEPS.length - 1, STEPS.findIndex((s) => s.id === st!.step) + 1)].id);
  if (d.bBack !== undefined) return go(STEPS[Math.max(0, STEPS.findIndex((s) => s.id === st!.step) - 1)].id);
  if (d.bSummary !== undefined) {
    st.summaryOpen = !st.summaryOpen;
    return renderAside();
  }
  if (d.bSuggest !== undefined) {
    if (!c.ancestryId || !c.classId) return deps.toast('Choose an ancestry and a class first (a background too, if you like)');
    st.awaitingSuggest = true;
    return deps.send({ t: 'previewBuild', choices: c, fill: true });
  }
  if (d.bCreate !== undefined) {
    if (st.mode === 'rebuild') {
      st.awaitingRebuild = true;
      return deps.cmd({ type: 'rebuild', choices: c });
    }
    return deps.send({ t: 'createCharacter', choices: c });
  }
  if (d.bAncestry) return change((x) => { x.ancestryId = d.bAncestry!; });
  if (d.bMode) return change((x) => { x.ancestryBoostMode = d.bMode as 'standard' | 'free'; });
  if (d.bBg) return change((x) => { x.backgroundId = d.bBg!; });
  if (d.bClass) {
    return change((x) => {
      x.classId = d.bClass!;
      const keys = deps.welcome()?.classes.find((k) => k.id === d.bClass)?.keyOptions ?? [];
      if (keys.length) x.keyAttribute = keys.some((k) => k.attribute === x.keyAttribute) ? x.keyAttribute : keys[0].attribute as Attribute;
      else delete x.keyAttribute;
    });
  }
  if (d.bKey) return change((x) => { x.keyAttribute = d.bKey as Attribute; });
  if (d.bAttr) {
    const [field, a] = d.bAttr.split(':') as [string, Attribute];
    return change((x) => {
      if (field === 'ancestry') {
        const anc = ancestry()?.ancestry;
        const need = x.ancestryBoostMode === 'standard' ? (anc ? anc.boosts.filter((b) => b === 'free').length : 1) : 2;
        x.ancestryBoosts = toggleIn(x.ancestryBoosts, a, need) as Attribute[];
      } else if (field === 'free') x.freeBoosts = toggleIn(x.freeBoosts, a, 4) as Attribute[];
      else if (field === 'bg0') {
        const second = x.backgroundBoosts[1] === a ? undefined : x.backgroundBoosts[1];
        x.backgroundBoosts = x.backgroundBoosts[0] === a ? [] : [a, ...(second ? [second] : [])];
      } else if (field === 'bg1') {
        const first = x.backgroundBoosts[0];
        if (first) x.backgroundBoosts = x.backgroundBoosts[1] === a ? [first] : [first, a];
      }
    });
  }
  if (d.bPick) {
    const [key, id] = d.bPick.split('|');
    return setPick(key, id);
  }
  if (d.bClassskill) {
    const [i, s] = d.bClassskill.split(':');
    return change((x) => {
      const next = [...(x.classSkills ?? [])];
      next[Number(i)] = s as SkillId;
      x.classSkills = next;
    });
  }
  if (d.bDrop) {
    const field = d.bDrop;
    return change((x) => {
      if (field === 'ancestryFeat') x.ancestryFeat = '';
      else if (field === 'classFeat') delete x.classFeat;
      else if (field === 'skillFeat') delete x.skillFeat;
      else if (field === 'classSkills') delete x.classSkills;
      else if (field === 'spells') x.spells = [];
      else if (field === 'backgroundSkillSwap') delete x.backgroundSkillSwap;
      else {
        const next = { ...x.featurePicks };
        delete next[field];
        x.featurePicks = next;
      }
    });
  }
  if (d.bSwap) return change((x) => { x.backgroundSkillSwap = d.bSwap as SkillId; });
  if (d.bSkill) {
    const count = st.preview?.skillChoices?.freeCount ?? 0;
    return change((x) => {
      if (x.skills.includes(d.bSkill as SkillId)) x.skills = x.skills.filter((k) => k !== d.bSkill);
      else if (x.skills.length < count) x.skills = [...x.skills, d.bSkill as SkillId];
      else deps.toast(`You can choose only ${count} free skill${count === 1 ? '' : 's'}: remove one first`);
    });
  }
  if (d.bSpell) {
    const sp = st.preview?.spells;
    const total = sp ? sp.cantrips + sp.rank1 + sp.extra : 0;
    return change((x) => {
      if (x.spells.includes(d.bSpell!)) x.spells = x.spells.filter((s) => s !== d.bSpell);
      else if (x.spells.length < total) x.spells = [...x.spells, d.bSpell!];
      else deps.toast(`You can choose only ${total} spells: remove one first`);
    });
  }
}
